<template>
  <BaseVeeForm
    :action="submitForm"
    ensure-error-keys-camel-case
    v-slot="{ isPending, invalid }"
    class="space-y-8"
  >
    <div class="space-y-4">
      <ValidationProvider rules="required" name="name" slim vid="name">
        <template #default="{ errors }">
          <BaseFormGroup>
            <BaseFormLabel> Name </BaseFormLabel>
            <BaseFormInlineMessage info>
              A name to easily distinguish the service.
            </BaseFormInlineMessage>
            <BaseFormInput
              type="text"
              v-model="formData.name"
              readonly
              class="w-2/3"
              @focus="$event.target.removeAttribute('readonly')"
            />
            <BaseFormInlineMessage v-if="errors.length" danger>
              {{ errors[0] }}
            </BaseFormInlineMessage>
          </BaseFormGroup>
        </template>
      </ValidationProvider>
      <ValidationProvider rules="required" name="domain" slim vid="domain">
        <template #default="{ errors }">
          <BaseFormGroup>
            <BaseFormLabel> Domain </BaseFormLabel>
            <BaseFormInlineMessage info> Softone URL </BaseFormInlineMessage>
            <BaseFormInput
              type="url"
              v-model="formData.domain"
              readonly
              class="w-2/3"
              @focus="$event.target.removeAttribute('readonly')"
            />
            <BaseFormInlineMessage v-if="errors.length" danger>
              {{ errors[0] }}
            </BaseFormInlineMessage>
          </BaseFormGroup>
        </template>
      </ValidationProvider>
      <ValidationProvider rules="required" name="application id" slim vid="application_id">
        <template #default="{ errors }">
          <BaseFormGroup>
            <BaseFormLabel> Application ID </BaseFormLabel>
            <BaseFormInput
              type="text"
              v-model="formData.applicationId"
              readonly
              class="w-2/3"
              @focus="$event.target.removeAttribute('readonly')"
            />
            <BaseFormInlineMessage v-if="errors.length" danger>
              {{ errors[0] }}
            </BaseFormInlineMessage>
          </BaseFormGroup>
        </template>
      </ValidationProvider>
      <ValidationProvider rules="required" name="username" slim vid="username">
        <template #default="{ errors }">
          <BaseFormGroup>
            <BaseFormLabel> Username </BaseFormLabel>
            <BaseFormInput
              type="text"
              v-model="formData.username"
              readonly
              class="w-2/3"
              @focus="$event.target.removeAttribute('readonly')"
            />
            <BaseFormInlineMessage v-if="errors.length" danger>
              {{ errors[0] }}
            </BaseFormInlineMessage>
          </BaseFormGroup>
        </template>
      </ValidationProvider>
      <ValidationProvider rules="required" name="password" slim vid="password">
        <template #default="{ errors }">
          <BaseFormGroup>
            <BaseFormLabel> Password </BaseFormLabel>
            <div class="flex space-x-2">
              <BaseFormInput
                :type="showPassword ? 'text' : 'password'"
                v-model="formData.password"
                class="w-2/3"
                @focus="$event.target.removeAttribute('readonly')"
              />
              <BaseButton
                type="button"
                primary
                outline
                @click="setShowConsumerSecret(!showPassword)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="icon icon-tabler icon-tabler-eye"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  stroke-width="2"
                  stroke="currentColor"
                  fill="none"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <template v-if="showPassword">
                    <line x1="3" y1="3" x2="21" y2="21" />
                    <path d="M10.584 10.587a2 2 0 0 0 2.828 2.83" />
                    <path
                      d="M9.363 5.365a9.466 9.466 0 0 1 2.637 -.365c4
                      0 7.333 2.333 10 7c-.778 1.361 -1.612 2.524 -2.503
                      3.488m-2.14 1.861c-1.631 1.1 -3.415 1.651 -5.357
                      1.651c-4 0 -7.333 -2.333 -10 -7c1.369 -2.395 2.913 -4.175 4.632 -5.341"
                    />
                  </template>
                  <template v-else>
                    <circle cx="12" cy="12" r="2" />
                    <path
                      d="M22 12c-2.667 4.667 -6 7 -10 7s-7.333
                      -2.333 -10 -7c2.667 -4.667 6 -7 10 -7s7.333 2.333 10 7"
                    />
                  </template>
                </svg>
              </BaseButton>
            </div>
            <BaseFormInlineMessage v-if="errors.length" danger>
              {{ errors[0] }}
            </BaseFormInlineMessage>
          </BaseFormGroup>
        </template>
      </ValidationProvider>
      <ValidationProvider rules="required" name="products SQL name" slim vid="productsSqlName">
        <template #default="{ errors }">
          <BaseFormGroup>
            <BaseFormLabel> Products SQL name </BaseFormLabel>
            <div class="flex space-x-2">
              <BaseFormInput
                type="text"
                v-model="formData.productsSqlName"
                class="w-2/3"
                @focus="$event.target.removeAttribute('readonly')"
              />
            </div>
            <BaseFormInlineMessage v-if="errors.length" danger>
              {{ errors[0] }}
            </BaseFormInlineMessage>
          </BaseFormGroup>
        </template>
      </ValidationProvider>
      <ValidationProvider
        v-if="defaultData.id"
        rules="required"
        name="shipping cost product sku"
        slim
        vid="shipping_cost_product_sku"
      >
        <template #default="{ errors }">
          <BaseFormGroup>
            <BaseFormLabel> Shipping cost product sku </BaseFormLabel>
            <div class="flex space-x-2">
              <BaseFormInput
                type="text"
                v-model="formData.shippingCostProductSku"
                class="w-2/3"
                @focus="$event.target.removeAttribute('readonly')"
              />
            </div>
            <BaseFormInlineMessage v-if="errors.length" danger>
              {{ errors[0] }}
            </BaseFormInlineMessage>
          </BaseFormGroup>
        </template>
      </ValidationProvider>
    </div>
    <div class="space-x-2">
      <BaseButton primary :disabled="isPending || invalid"> Submit </BaseButton>

      <BaseButton type="button" ghost :disabled="isPending" @click="$emit('cancel')">
        Cancel
      </BaseButton>
    </div>
  </BaseVeeForm>
</template>

<script>
import { pick } from 'lodash-es';
import BaseVeeForm from 'Components/BaseVeeForm.vue';

export default {
  components: { BaseVeeForm },
  props: {
    defaultData: {
      type: Object,
      default: () => ({}),
    },
    action: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      showPassword: false,
      formData: {
        name: '',
        domain: '',
        applicationId: '',
        username: '',
        password: '',
        productsSqlName: '',
        shippingCostProductSku: '',
        ...this.getDefaultData(),
      },
    };
  },
  methods: {
    setShowConsumerSecret(value) {
      this.showPassword = Boolean(value);
    },
    getDefaultData() {
      const { config = {} } = this.defaultData || {};

      if (typeof config !== 'object' || config === null) {
        return {};
      }

      return pick(config, [
        'name',
        'domain',
        'applicationId',
        'username',
        'password',
        'productsSqlName',
        'shippingCostProductSku',
      ]);
    },
    async submitForm() {
      try {
        await this.action({ ...this.formData });
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
  },
};
</script>
